import type { ReactNode } from 'react';
import { Check } from 'lucide-react';
import { cn } from './cn';

/**
 * Where something is in a fixed sequence, and what is left.
 *
 * One primitive for every journey the product draws — a medical case from
 * request to certificate, a sample from collection to release — so that
 * "done", "here now" and "stuck" look the same wherever a reader meets them.
 *
 * Deliberately not colour-coded by clinical meaning. A step being complete is
 * not a fitness outcome, and borrowing the fit green for it would teach the
 * reader that green means "finished" on one screen and "fit" on the next.
 * Done is a quiet check, the current step is the primary colour, and a
 * blocked step is the warning tone with its reason written out.
 */
export type StepState = 'done' | 'current' | 'blocked' | 'upcoming';

export interface StepSpec {
  key: string;
  label: string;
  state: StepState;
  /** One short line under the label: a date, a venue, why it is blocked. */
  detail?: ReactNode;
}

export function Steps({ steps, label = 'Progress', orientation = 'auto', className }: {
  steps: StepSpec[];
  /** Names the list for a screen reader: "Case progress", "Sample progress". */
  label?: string;
  /**
   * `auto` lays the steps out in a row from `md` up and as a column below it,
   * where seven labels in a row would each get forty pixels.
   */
  orientation?: 'auto' | 'horizontal' | 'vertical';
  className?: string;
}) {
  const row = orientation === 'horizontal';
  const auto = orientation === 'auto';
  return (
    <ol
      aria-label={label}
      className={cn(
        'flex',
        row ? 'flex-row' : auto ? 'flex-col md:flex-row' : 'flex-col',
        className,
      )}
    >
      {steps.map((s, i) => {
        const last = i === steps.length - 1;
        return (
          <li
            key={s.key}
            aria-current={s.state === 'current' || s.state === 'blocked' ? 'step' : undefined}
            className={cn(
              'relative flex min-w-0 gap-3',
              row ? 'flex-1 flex-col items-start gap-2' : auto ? 'pb-4 md:flex-1 md:flex-col md:gap-2 md:pb-0' : 'pb-4',
              last && !row && 'pb-0',
            )}
          >
            {/* The rail between markers. Solid behind a finished step, dashed
                ahead of the current one, so the eye reads how far along it is
                before reading a single label. */}
            {!last && (
              <span
                aria-hidden
                className={cn(
                  'absolute',
                  row ? 'left-7 right-1 top-3 h-0 border-t-2'
                    : auto ? 'bottom-0 left-3 top-7 w-0 border-l-2 md:bottom-auto md:left-7 md:right-1 md:top-3 md:h-0 md:w-auto md:border-l-0 md:border-t-2'
                      : 'bottom-0 left-3 top-7 w-0 border-l-2',
                  s.state === 'done' ? 'border-neutral-400' : 'border-dashed border-neutral-300',
                )}
              />
            )}
            <Marker state={s.state} index={i + 1} />
            <div className={cn('min-w-0', row || auto ? 'md:pr-3' : '')}>
              <div
                className={cn(
                  'text-small',
                  s.state === 'current' && 'font-semibold text-primary-700',
                  s.state === 'blocked' && 'font-semibold text-warning-600',
                  s.state === 'done' && 'font-medium text-neutral-800',
                  s.state === 'upcoming' && 'text-neutral-500',
                )}
              >
                {s.label}
                <span className="sr-only">
                  {s.state === 'done' ? ' — done' : s.state === 'current' ? ' — in progress'
                    : s.state === 'blocked' ? ' — needs attention' : ' — not started'}
                </span>
              </div>
              {s.detail && (
                <div className={cn('mt-0.5 text-caption font-normal',
                  s.state === 'blocked' ? 'text-warning-600' : 'text-neutral-500')}
                >
                  {s.detail}
                </div>
              )}
            </div>
          </li>
        );
      })}
    </ol>
  );
}

function Marker({ state, index }: { state: StepState; index: number }) {
  return (
    <span
      aria-hidden
      className={cn(
        'relative z-[1] flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 text-micro tabular-nums',
        state === 'done' && 'border-neutral-400 bg-neutral-400 text-white',
        state === 'current' && 'border-primary-600 bg-white text-primary-700 ring-4 ring-primary-100',
        state === 'blocked' && 'border-warning-600 bg-warning-100 text-warning-600',
        state === 'upcoming' && 'border-neutral-300 bg-white text-neutral-500',
      )}
    >
      {state === 'done' ? <Check className="h-3.5 w-3.5" strokeWidth={3} />
        : state === 'blocked' ? '!'
          : index}
    </span>
  );
}

/**
 * Labelled facts, in a grid that folds to one column on a phone.
 *
 * The detail pages each built this as a `<dl>` of their own, with the label
 * above the value on one screen and beside it on the next. Label above, value
 * below, always: a reader scanning for "Appointment" should find it in the
 * same place relative to its value on every page.
 */
export interface Fact {
  label: string;
  value: ReactNode;
  /** A second line, quieter than the value: a city, a status, a date. */
  hint?: ReactNode;
  /** Spans the whole row — for a note or a long address. */
  wide?: boolean;
}

export function Facts({ facts, columns = 3, className }: {
  facts: Array<Fact | false | null | undefined>;
  columns?: 2 | 3 | 4;
  className?: string;
}) {
  const shown = facts.filter(Boolean) as Fact[];
  return (
    <dl
      className={cn(
        'grid gap-x-6 gap-y-4 sm:grid-cols-2',
        columns === 3 && 'lg:grid-cols-3',
        columns === 4 && 'lg:grid-cols-4',
        className,
      )}
    >
      {shown.map((f) => (
        <div key={f.label} className={cn('min-w-0', f.wide && 'sm:col-span-full')}>
          <dt className="text-caption text-neutral-500">{f.label}</dt>
          <dd className="mt-0.5 break-words text-body text-neutral-900 [overflow-wrap:anywhere]">{f.value}</dd>
          {f.hint && <dd className="text-caption font-normal text-neutral-500">{f.hint}</dd>}
        </div>
      ))}
    </dl>
  );
}
